import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Storefront, Eye, EyeSlash, WarningCircle, Info } from "@phosphor-icons/react";
import { customerAuth } from "../lib/customerAuth";
import { useAuth } from "../lib/auth";

export default function CustomerLogin() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [banner, setBanner] = useState("");

  useEffect(() => {
    // Surfaced Google-auth denial from AuthCallback (Phase 2).
    try {
      const m = sessionStorage.getItem("gf_login_error");
      if (m) { setBanner(m); sessionStorage.removeItem("gf_login_error"); }
    } catch { /* noop */ }
    // Surface success message from reset flow.
    try {
      const m = sessionStorage.getItem("gf_login_notice");
      if (m) { setBanner(m); sessionStorage.removeItem("gf_login_notice"); }
    } catch { /* noop */ }
  }, []);

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePassword = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setBusy(true); setErr("");
    try {
      await customerAuth.login(email.trim().toLowerCase(), password);
      setPassword("");   // clear the field per spec I
      // Confirm the server-side session before entering the portal.
      const u = await refresh();
      if (u?.customer_id) nav("/portal");
      else setErr("You signed in, but your portal could not be opened. Please try again or contact support.");
      return;
    } catch (ex) {
      setPassword("");
      // Backend already returns the generic message; keep it verbatim.
      setErr(ex.status === 429 ? ex.message : "Email or password is incorrect.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4" data-testid="customer-login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-6">
          <Storefront size={26} className="text-emerald-700" weight="bold" />
          <div className="text-emerald-900 text-lg font-semibold tracking-tight">Get Fuel Card — Customer Portal</div>
        </div>
        <div className="rounded-2xl bg-white shadow-lg border border-slate-200 overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <h1 className="text-xl font-semibold text-slate-900">Sign in</h1>
            <p className="text-sm text-slate-500 mt-1">
              Use Google or your customer portal password.
            </p>
          </div>
          <div className="px-6 py-4 space-y-3">
            {banner && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2" data-testid="customer-login-notice">
                <Info size={16} weight="bold" className="mt-[1px] shrink-0" /><span>{banner}</span>
              </div>
            )}
            {err && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 flex items-start gap-2" role="alert" data-testid="customer-login-error">
                <WarningCircle size={16} weight="bold" className="mt-[1px] shrink-0" /><span>{err}</span>
              </div>
            )}

            <button
              onClick={handleGoogle}
              type="button"
              data-testid="customer-login-google-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-900 hover:bg-slate-50"
            >
              <span className="text-lg">🇬</span> Continue with Google
            </button>

            <div className="flex items-center gap-3 py-1">
              <div className="flex-1 h-px bg-slate-200" />
              <div className="text-xs text-slate-500 uppercase tracking-wide">or</div>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <form onSubmit={handlePassword} className="space-y-3" data-testid="customer-login-form">
              <div>
                <label htmlFor="cl-email" className="text-xs font-medium text-slate-700 block mb-1">Email</label>
                <input
                  id="cl-email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="customer-login-email"
                  className="w-full rounded-lg border border-slate-300 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label htmlFor="cl-pass" className="text-xs font-medium text-slate-700 block mb-1">Password</label>
                <div className="relative">
                  <input
                    id="cl-pass"
                    type={show ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="customer-login-password"
                    className="w-full rounded-lg border border-slate-300 text-sm px-3 py-2 pr-9 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <button type="button" onClick={() => setShow(v => !v)}
                          data-testid="customer-login-toggle-show"
                          aria-label={show ? "Hide password" : "Show password"}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800">
                    {show ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="customer-login-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
              >
                {busy ? "Signing in…" : "Sign in"}
              </button>
              <div className="flex items-center justify-between text-xs">
                <Link to="/customer/forgot-password" className="text-emerald-700 hover:underline" data-testid="customer-login-forgot-link">
                  Forgot password?
                </Link>
                <span className="text-slate-400">·</span>
                <span className="text-slate-500">
                  Have an activation link? Just open it.
                </span>
              </div>
            </form>
          </div>
          <div className="px-6 py-3 border-t border-slate-100 text-xs text-slate-500 flex items-center justify-between">
            <span>Team member?</span>
            <Link to="/login" className="text-slate-800 hover:underline" data-testid="customer-login-team-link">
              Team login →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
